import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "@phosphor-icons/react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { resolveAssetUrl } from "@/lib/api";

// Session-based dismissal keyed by content hash — αν αλλάξει το popup από
// το dashboard (τίτλος/μήνυμα/εικόνα), εμφανίζεται ξανά στο επόμενο visit.
function contentHash(popup) {
  return [popup.title, popup.message, popup.image_url, popup.button_text, popup.button_link].join("|");
}

export default function PopupModal() {
  const content = useSiteContent();
  const popup = content?.popup;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!popup?.enabled) return;
    const key = "mp_popup_dismissed_" + btoa(unescape(encodeURIComponent(contentHash(popup)))).slice(0, 24);
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [popup]);

  const close = () => {
    if (popup) {
      const key = "mp_popup_dismissed_" + btoa(unescape(encodeURIComponent(contentHash(popup)))).slice(0, 24);
      try { sessionStorage.setItem(key, "1"); } catch {}
    }
    setOpen(false);
  };

  if (!popup?.enabled || !open) return null;

  const isInternal = popup.button_link && popup.button_link.startsWith("/");

  return (
    <div
      data-testid="site-popup"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm animate-[fadeIn_.25s_ease]"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[#141414] border border-white/10 shadow-[0_20px_60px_rgba(229,0,0,0.25)] overflow-hidden"
      >
        <button
          onClick={close}
          data-testid="popup-close-btn"
          aria-label="Κλείσιμο"
          className="absolute top-3 right-3 w-9 h-9 bg-black/60 border border-white/15 flex items-center justify-center hover:bg-[#E50000] hover:border-[#E50000] transition-colors z-10"
        >
          <X size={16} weight="bold" />
        </button>
        {popup.image_url && (
          <div className="w-full aspect-[16/9] bg-[#0A0A0A]">
            <img
              src={resolveAssetUrl(popup.image_url)}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6 md:p-8">
          {popup.title && (
            <h2 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl leading-[1.05]">
              {popup.title}
            </h2>
          )}
          {popup.message && (
            <p className="mt-4 text-white/70 leading-relaxed text-sm md:text-base whitespace-pre-line">
              {popup.message}
            </p>
          )}
          {popup.button_text && popup.button_link && (
            <div className="mt-8">
              {isInternal ? (
                <Link
                  to={popup.button_link}
                  data-testid="popup-cta-btn"
                  onClick={close}
                  className="btn-primary text-xs inline-flex"
                >
                  {popup.button_text}
                </Link>
              ) : (
                <a
                  href={popup.button_link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="popup-cta-btn"
                  onClick={close}
                  className="btn-primary text-xs inline-flex"
                >
                  {popup.button_text}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
